import React, { useEffect, useRef } from 'react';
import styled from 'styled-components';
import { theme, cssVar } from 'styles/theme';

const WHEEL_STEP = 40;

const Shell = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  flex-shrink: 0;
  min-width: 0;
  padding: 4px 8px 6px;
  border: 1px solid ${cssVar('color-border', theme.colors.border)};
  border-radius: ${theme.borderRadius.md};
  background: ${cssVar('color-bg-white', theme.colors.white)};
  outline: none;
`;

const Row = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
`;

const Label = styled.span`
  flex-shrink: 0;
  width: ${theme.components.form.searchLabelWidth};
  text-align: right;
  font-size: ${theme.typography.fontSize.sm};
  color: ${cssVar('color-text-secondary', theme.colors.text.secondary)};
`;

const AxisGroup = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  min-width: 0;
`;

const Chip = styled.button`
  appearance: none;
  flex-shrink: 0;
  padding: ${theme.components.actionButton.paddingY} ${theme.components.actionButton.paddingX};
  min-height: ${theme.components.actionButton.minHeight};
  border: 1px solid
    ${(p) =>
      p.$active
        ? cssVar('color-primary', theme.colors.primary)
        : cssVar('color-border', theme.colors.border)};
  border-radius: ${theme.borderRadius.sm};
  background: ${(p) =>
    p.$active
      ? cssVar('color-primary-light', theme.colors.primaryLight)
      : cssVar('color-bg-white', theme.colors.white)};
  color: ${(p) =>
    p.$active
      ? cssVar('color-primary-dark', theme.colors.primaryDark)
      : cssVar('color-text', theme.colors.text.primary)};
  font-size: ${theme.typography.fontSize.sm};
  font-weight: ${(p) => (p.$active ? 600 : 500)};
  cursor: ${(p) => (p.disabled ? 'default' : 'pointer')};
  white-space: nowrap;
`;

const MemberStrip = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  min-width: 0;
  overflow-x: auto;
  flex: 1;
`;

const JumpSelect = styled.select`
  flex-shrink: 0;
  min-height: ${theme.components.actionButton.minHeight};
  max-width: 220px;
  padding: 0 6px;
  border: 1px solid ${cssVar('color-border', theme.colors.border)};
  border-radius: ${theme.borderRadius.sm};
  background: ${cssVar('color-bg-input', theme.colors.background.input)};
  color: ${cssVar('color-text', theme.colors.text.primary)};
  font-size: ${theme.typography.fontSize.sm};
`;

const Meta = styled.div`
  font-size: ${theme.typography.fontSize.xs};
  color: ${cssVar('color-text-secondary', theme.colors.text.secondary)};
  padding-left: ${theme.components.form.searchLabelWidth};
`;

/**
 * 조회 축(1개) + 멤버 슬라이스 바.
 * 휠·좌우키는 멤버만 바꾸고 FormArea 값은 건드리지 않는다.
 */
export default function AxisSliceBar({
  axes = [],
  axisId,
  onAxisChange,
  members = [],
  memberKey,
  onMemberChange,
  disabled = false,
  hint = '',
}) {
  const shellRef = useRef(null);
  const activeRef = useRef(null);
  const accRef = useRef(0);
  const membersRef = useRef(members);
  const memberKeyRef = useRef(memberKey);
  const disabledRef = useRef(disabled);
  const onMemberChangeRef = useRef(onMemberChange);
  membersRef.current = members;
  memberKeyRef.current = memberKey;
  disabledRef.current = disabled;
  onMemberChangeRef.current = onMemberChange;

  useEffect(() => {
    activeRef.current?.scrollIntoView?.({ inline: 'nearest', block: 'nearest' });
  }, [memberKey]);

  useEffect(() => {
    const el = shellRef.current;
    if (!el) return undefined;
    const onWheel = (e) => {
      if (disabledRef.current) return;
      if (e.target?.tagName === 'SELECT') return;
      const list = membersRef.current;
      if (list.length < 2) return;
      e.preventDefault();
      e.stopPropagation();
      accRef.current += e.deltaY;
      if (Math.abs(accRef.current) < WHEEL_STEP) return;
      const dir = accRef.current > 0 ? 1 : -1;
      accRef.current = 0;
      const idx = list.findIndex((m) => m.key === memberKeyRef.current);
      const next = ((idx < 0 ? 0 : idx) + dir + list.length) % list.length;
      onMemberChangeRef.current?.(list[next].key);
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, []);

  const handleKeyDown = (e) => {
    if (disabled || members.length < 2) return;
    if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
    e.preventDefault();
    const dir = e.key === 'ArrowRight' ? 1 : -1;
    const idx = members.findIndex((m) => m.key === memberKey);
    const next = ((idx < 0 ? 0 : idx) + dir + members.length) % members.length;
    onMemberChange?.(members[next].key);
  };

  const active = members.find((m) => m.key === memberKey);
  const pos =
    members.length && active
      ? `${members.findIndex((m) => m.key === memberKey) + 1}/${members.length}`
      : '0/0';

  return (
    <Shell
      ref={shellRef}
      tabIndex={0}
      onKeyDown={handleKeyDown}
      title="마우스 휠 또는 ← → 로 멤버 전환"
    >
      <Row>
        <Label>조회축</Label>
        <AxisGroup>
          {axes.map((ax) => (
            <Chip
              key={ax.id}
              type="button"
              $active={ax.id === axisId}
              onClick={() => onAxisChange?.(ax.id)}
            >
              {ax.label}
            </Chip>
          ))}
        </AxisGroup>
      </Row>
      <Row>
        <Label>슬라이스</Label>
        <MemberStrip>
          {members.length === 0 ? (
            <Chip type="button" disabled>
              {disabled ? '조회 후 전환' : '멤버 없음'}
            </Chip>
          ) : (
            members.map((m) => (
              <Chip
                key={m.key}
                type="button"
                ref={m.key === memberKey ? activeRef : undefined}
                $active={m.key === memberKey}
                disabled={disabled}
                onClick={() => onMemberChange?.(m.key)}
                title={m.label}
              >
                {m.label}
                {typeof m.count === 'number' ? ` (${m.count})` : ''}
              </Chip>
            ))
          )}
        </MemberStrip>
        {members.length > 12 ? (
          <JumpSelect
            value={memberKey || ''}
            disabled={disabled}
            onChange={(e) => onMemberChange?.(e.target.value)}
            title="멤버로 바로 이동"
          >
            {members.map((m) => (
              <option key={m.key} value={m.key}>
                {m.label}
                {typeof m.count === 'number' ? ` (${m.count})` : ''}
              </option>
            ))}
          </JumpSelect>
        ) : null}
      </Row>
      <Meta>
        {pos}
        {active ? ` · ${active.label}` : ''}
        {hint ? ` · ${hint}` : ''}
      </Meta>
    </Shell>
  );
}
